import { useState, useCallback, useRef, useEffect } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  Save,
  Percent,
  ChevronRight,
  ChevronDown,
  Search,
  X,
} from 'lucide-react';
import {
  useGuardrailUsages,
  useAddGuardrailUsage,
  useUpdateGuardrailUsage,
  useRemoveGuardrailUsage,
  type GuardrailUsage,
} from '@/hooks/useGuardrailUsages';
import {
  useGuardrailDefinitions,
  type GuardrailDefinition,
} from '@/hooks/useGuardrailDefinitions';
import { useEditMode } from '@/contexts/EditModeContext';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { cn } from '@/lib/utils';

export function CollapsibleMultipliers() {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="card overflow-hidden !p-0">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-3 md:px-4 py-2 md:py-3 hover:bg-gray-50"
      >
        <div className="flex items-center gap-2">
          <Percent className="h-4 w-4 text-gray-400" />
          <span className="font-medium text-gray-700">Global Guardrails</span>
          <span className="text-xs text-gray-500 hidden md:inline">— global guardrails applied across all activities</span>
        </div>
        {expanded ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
      </button>
      {expanded && (
        <div className="border-t border-gray-200 p-4">
          <MultipliersTab />
        </div>
      )}
    </div>
  );
}

function MultipliersTab() {
  const { data: usagesData, isLoading } = useGuardrailUsages('global');
  const { data: definitionsData } = useGuardrailDefinitions();
  const addUsage = useAddGuardrailUsage();
  const updateUsage = useUpdateGuardrailUsage();
  const removeUsage = useRemoveGuardrailUsage();
  const { isEditMode } = useEditMode();

  const [showPicker, setShowPicker] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');
  const [editingUsage, setEditingUsage] = useState<GuardrailUsage | null>(null);
  const [configuring, setConfiguring] = useState<{ definitionId: string; definition: GuardrailDefinition } | null>(null);

  // Factor configuration form state
  const [baselineValue, setBaselineValue] = useState('0');
  const [multiplierFactor, setMultiplierFactor] = useState('0');
  const [multiplierStep, setMultiplierStep] = useState('1');
  const [multiplierLabel, setMultiplierLabel] = useState('');
  const [optionFactors, setOptionFactors] = useState<Record<string, string>>({});
  const [defaultValue, setDefaultValue] = useState('');

  const usages = usagesData?.usages || [];
  const definitions = definitionsData?.definitions || [];

  // Available definitions = active definitions not already assigned globally
  const assignedDefIds = new Set(usages.map((u) => u.definitionId));
  const availableDefinitions = definitions.filter(
    (d) => d.isActive && !assignedDefIds.has(d.id)
  );
  const filteredDefinitions = pickerSearch
    ? availableDefinitions.filter(
        (d) =>
          d.name.toLowerCase().includes(pickerSearch.toLowerCase()) ||
          d.code.toLowerCase().includes(pickerSearch.toLowerCase())
      )
    : availableDefinitions;

  const pickerRef = useRef<HTMLDivElement>(null);

  // Close picker on click outside
  useEffect(() => {
    if (!showPicker) return;
    const handler = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setShowPicker(false);
        setPickerSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showPicker]);

  // Open configuration modal for a definition (new)
  const handlePickDefinition = useCallback((def: GuardrailDefinition) => {
    setShowPicker(false);
    setPickerSearch('');
    setConfiguring({ definitionId: def.id, definition: def });
    setEditingUsage(null);
    // Reset form
    setBaselineValue('0');
    setMultiplierFactor('0');
    setMultiplierStep('1');
    setMultiplierLabel('');
    setOptionFactors({});
    setDefaultValue('');
  }, []);

  // Open configuration modal for an existing usage (edit)
  const handleEditUsage = useCallback((usage: GuardrailUsage) => {
    setEditingUsage(usage);
    setConfiguring(null);
    setBaselineValue(usage.baselineValue?.toString() || '0');
    setMultiplierFactor(usage.multiplierFactor?.toString() || '0');
    setMultiplierStep(usage.multiplierStep?.toString() || '1');
    setMultiplierLabel(usage.multiplierLabel || '');
    setOptionFactors(
      usage.optionFactors
        ? Object.fromEntries(Object.entries(usage.optionFactors).map(([k, v]) => [k, v.toString()]))
        : {}
    );
    setDefaultValue(usage.defaultValue || '');
  }, []);

  const closeModal = useCallback(() => {
    setConfiguring(null);
    setEditingUsage(null);
  }, []);

  // Save: either add new usage or update existing
  const handleSaveConfig = useCallback(async () => {
    try {
      const mf = parseFloat(multiplierFactor) || 0;
      const bv = parseFloat(baselineValue) || 0;
      const ms = parseFloat(multiplierStep) || 1;

      // Determine inputType from configuring or editingUsage
      const inputType = configuring?.definition.inputType || editingUsage?.inputType || 'number';

      // Build option factors for select type
      let parsedOptionFactors: Record<string, number> | undefined;
      if (inputType === 'select') {
        parsedOptionFactors = {};
        const opts = configuring?.definition.options || editingUsage?.options || [];
        for (const opt of opts) {
          parsedOptionFactors[opt] = parseFloat(optionFactors[opt] || '0') || 0;
        }
      }

      if (editingUsage) {
        // Update existing
        await updateUsage.mutateAsync({
          id: editingUsage.id,
          baselineValue: inputType === 'number' ? bv : undefined,
          multiplierFactor: mf || undefined,
          multiplierStep: inputType === 'number' ? ms : undefined,
          multiplierLabel: multiplierLabel || undefined,
          optionFactors: parsedOptionFactors,
          defaultValue: defaultValue || undefined,
        });
      } else if (configuring) {
        // Create new usage
        await addUsage.mutateAsync({
          definitionId: configuring.definitionId,
          scope: 'global',
          baselineValue: inputType === 'number' ? bv : undefined,
          multiplierFactor: mf || undefined,
          multiplierStep: inputType === 'number' ? ms : undefined,
          multiplierLabel: multiplierLabel || undefined,
          optionFactors: parsedOptionFactors,
          defaultValue: defaultValue || undefined,
        });
      }
      closeModal();
    } catch (e) {
      const err = e as { message?: string };
      alert(`Failed: ${err.message || 'unknown error'}`);
    }
  }, [configuring, editingUsage, baselineValue, multiplierFactor, multiplierStep, multiplierLabel, optionFactors, defaultValue, addUsage, updateUsage, closeModal]);

  const handleRemoveUsage = useCallback(async (usage: GuardrailUsage) => {
    if (!confirm(`Remove global guardrail "${usage.name}"?\n\nThis will detach it from the global scope. The definition remains available for re-use.`)) return;
    try {
      await removeUsage.mutateAsync(usage.id);
    } catch (e) {
      const err = e as { message?: string };
      alert(`Failed: ${err.message || 'unknown error'}`);
    }
  }, [removeUsage]);

  // The modal definition: either from configuring (new) or from editingUsage (edit)
  const modalDef = configuring?.definition || (editingUsage
    ? {
        inputType: editingUsage.inputType,
        options: editingUsage.options,
        name: editingUsage.name,
        code: editingUsage.code,
        description: editingUsage.description,
      }
    : null);
  const showModal = !!(configuring || editingUsage);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Global Guardrails</h2>
          <p className="text-sm text-gray-500">
            Pick guardrails from the definitions catalogue and configure their factor values.
            {!isEditMode && ' (Read-only -- enter Edit Services Framework to modify.)'}
          </p>
        </div>
        {isEditMode && (
          <div className="relative" ref={pickerRef}>
            <button
              onClick={() => setShowPicker(!showPicker)}
              className="btn-primary flex items-center gap-2"
              disabled={availableDefinitions.length === 0}
            >
              <Plus className="h-4 w-4" />
              Add Guardrail
            </button>

            {/* Picker dropdown */}
            {showPicker && (
              <div className="absolute right-0 top-full mt-1 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                <div className="p-2 border-b border-gray-200">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      value={pickerSearch}
                      onChange={(e) => setPickerSearch(e.target.value)}
                      placeholder="Search definitions..."
                      className="input w-full pl-8 text-sm"
                      autoFocus
                    />
                  </div>
                </div>
                <div className="max-h-60 overflow-y-auto">
                  {filteredDefinitions.length === 0 ? (
                    <p className="px-3 py-4 text-sm text-gray-500 text-center">
                      {availableDefinitions.length === 0
                        ? 'All definitions are already assigned.'
                        : 'No matching definitions.'}
                    </p>
                  ) : (
                    filteredDefinitions.map((def) => (
                      <button
                        key={def.id}
                        onClick={() => handlePickDefinition(def)}
                        className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                      >
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs text-gray-500">{def.code}</span>
                          <span className={cn(
                            'text-[10px] uppercase px-1.5 py-0.5 rounded font-medium',
                            def.inputType === 'number' && 'bg-blue-100 text-blue-700',
                            def.inputType === 'select' && 'bg-purple-100 text-purple-700',
                            def.inputType === 'boolean' && 'bg-amber-100 text-amber-700',
                          )}>
                            {def.inputType}
                          </span>
                        </div>
                        <div className="text-sm font-medium text-gray-900 mt-0.5">{def.name}</div>
                        {def.description && (
                          <div className="text-xs text-gray-500 mt-0.5 line-clamp-1">{def.description}</div>
                        )}
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Usages Table */}
      {usages.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-8 text-center">
          <Percent className="mx-auto h-12 w-12 text-gray-300" />
          <p className="mt-2 text-sm text-gray-500">No global guardrails assigned yet.</p>
          {isEditMode && availableDefinitions.length > 0 && (
            <p className="mt-1 text-xs text-gray-400">
              Click "Add Guardrail" to pick from the definitions catalogue.
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {usages.map((u) => (
            <div key={u.id} className="p-3 rounded-lg border border-gray-200 bg-gray-50 group">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <span className="font-medium text-gray-900">{u.name}</span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="font-mono text-xs text-gray-600">{u.code}</span>
                  {isEditMode && (
                    <>
                      <button
                        onClick={() => handleEditUsage(u)}
                        className="opacity-100 md:opacity-0 md:group-hover:opacity-100 p-1 text-gray-400 hover:text-gray-600"
                        title="Configure factors"
                      >
                        <Pencil className="h-3 w-3" />
                      </button>
                      <button
                        onClick={() => handleRemoveUsage(u)}
                        className="opacity-100 md:opacity-0 md:group-hover:opacity-100 p-1 text-gray-400 hover:text-red-600"
                        title="Remove"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                {u.baselineValue != null && <span>Baseline: {u.baselineValue}</span>}
                <span>{'\u00B7'} Factor: {u.multiplierFactor != null ? `+${u.multiplierFactor}x` : '\u2014'} {u.multiplierLabel || ''}</span>
                {u.multiplierStep != null && u.multiplierStep > 1 && <span>{'\u00B7'} Step: {u.multiplierStep}</span>}
                <span>{'\u00B7'} Type: {u.inputType}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Factor Configuration Modal */}
      {showModal && modalDef && (
        <div className="dialog-overlay">
          <div className="dialog max-w-lg">
            <div className="dialog-header">
              <h2 className="dialog-title">
                {editingUsage ? 'Edit Guardrail Factors' : 'Configure Guardrail'}
              </h2>
              <button onClick={closeModal} className="p-1 rounded hover:bg-gray-100">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="dialog-body space-y-4">
              {/* Definition info (read-only) */}
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs text-gray-500">{modalDef.code}</span>
                  <span className={cn(
                    'text-[10px] uppercase px-1.5 py-0.5 rounded font-medium',
                    modalDef.inputType === 'number' && 'bg-blue-100 text-blue-700',
                    modalDef.inputType === 'select' && 'bg-purple-100 text-purple-700',
                    modalDef.inputType === 'boolean' && 'bg-amber-100 text-amber-700',
                  )}>
                    {modalDef.inputType}
                  </span>
                </div>
                <div className="text-sm font-medium text-gray-900 mt-1">{modalDef.name}</div>
                {modalDef.description && (
                  <div className="text-xs text-gray-500 mt-1">{modalDef.description}</div>
                )}
              </div>

              {/* Factor fields by input type */}
              {modalDef.inputType === 'number' && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="label">Baseline Value</label>
                      <input
                        type="number"
                        value={baselineValue}
                        onChange={(e) => setBaselineValue(e.target.value)}
                        className="input mt-1 w-full"
                        placeholder="0"
                        step="any"
                      />
                      <p className="text-[10px] text-gray-400 mt-0.5">Included threshold</p>
                    </div>
                    <div>
                      <label className="label">Factor per Step</label>
                      <input
                        type="number"
                        value={multiplierFactor}
                        onChange={(e) => setMultiplierFactor(e.target.value)}
                        className="input mt-1 w-full"
                        placeholder="0.5"
                        step="0.05"
                        min="0"
                      />
                      <p className="text-[10px] text-gray-400 mt-0.5">e.g., 0.5 = +0.5x</p>
                    </div>
                    <div>
                      <label className="label">Step Size</label>
                      <input
                        type="number"
                        value={multiplierStep}
                        onChange={(e) => setMultiplierStep(e.target.value)}
                        className="input mt-1 w-full"
                        placeholder="1"
                        step="any"
                        min="1"
                      />
                      <p className="text-[10px] text-gray-400 mt-0.5">Units per step</p>
                    </div>
                  </div>
                  <div>
                    <label className="label">Display Label</label>
                    <input
                      type="text"
                      value={multiplierLabel}
                      onChange={(e) => setMultiplierLabel(e.target.value)}
                      className="input mt-1 w-full"
                      placeholder="e.g., per additional DC"
                    />
                  </div>
                  <div>
                    <label className="label">Default Value</label>
                    <input
                      type="text"
                      value={defaultValue}
                      onChange={(e) => setDefaultValue(e.target.value)}
                      className="input mt-1 w-full"
                    />
                  </div>
                </>
              )}

              {modalDef.inputType === 'boolean' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="label">Factor When Enabled</label>
                    <input
                      type="number"
                      value={multiplierFactor}
                      onChange={(e) => setMultiplierFactor(e.target.value)}
                      className="input mt-1 w-full"
                      placeholder="0.5"
                      step="0.05"
                      min="0"
                    />
                    <p className="text-[10px] text-gray-400 mt-0.5">Added when toggled on</p>
                  </div>
                  <div>
                    <label className="label">Display Label</label>
                    <input
                      type="text"
                      value={multiplierLabel}
                      onChange={(e) => setMultiplierLabel(e.target.value)}
                      className="input mt-1 w-full"
                      placeholder="e.g., if required"
                    />
                  </div>
                </div>
              )}

              {modalDef.inputType === 'select' && modalDef.options && (
                <>
                  <div>
                    <label className="label">Options (from definition)</label>
                    <div className="text-sm text-gray-700 mt-1 bg-gray-50 rounded px-3 py-2 font-mono">
                      {modalDef.options.join(', ')}
                    </div>
                  </div>
                  <div>
                    <label className="label">Factor per Option</label>
                    <div className="space-y-2 mt-1">
                      {modalDef.options.map((opt) => (
                        <div key={opt} className="flex items-center gap-3">
                          <span className="text-sm text-gray-700 w-32 truncate">{opt}</span>
                          <input
                            type="number"
                            value={optionFactors[opt] || '0'}
                            onChange={(e) =>
                              setOptionFactors((prev) => ({
                                ...prev,
                                [opt]: e.target.value,
                              }))
                            }
                            className="input w-24 text-center"
                            step="0.05"
                            min="0"
                            placeholder="0"
                          />
                          <span className="text-xs text-gray-400">x</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="label">Display Label</label>
                    <input
                      type="text"
                      value={multiplierLabel}
                      onChange={(e) => setMultiplierLabel(e.target.value)}
                      className="input mt-1 w-full"
                      placeholder="e.g., for complex/regulated"
                    />
                  </div>
                </>
              )}
            </div>
            <div className="dialog-footer">
              <button type="button" onClick={closeModal} className="btn-secondary">
                Cancel
              </button>
              <button
                onClick={handleSaveConfig}
                className="btn-primary flex items-center gap-2"
                disabled={addUsage.isPending || updateUsage.isPending}
              >
                <Save className="h-4 w-4" />
                {editingUsage ? 'Save' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Display a compact summary of the factor values for a usage row */
